// components/Checkout/OrderReview.tsx
import { FaLock, FaCheckCircle } from 'react-icons/fa';

interface OrderReviewProps {
  items: any[];
  total: number;
  paymentMethod: string;
  isProcessing: boolean;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function OrderReview({
  items,
  total,
  paymentMethod,
  isProcessing,
  onBack,
  onSubmit,
}: OrderReviewProps) {
  const getPaymentLabel = () => {
    const labels: Record<string, string> = {
      cod: 'Cash on Delivery',
      bml: 'BML Payment',
      bank: 'Bank Transfer',
      paypal: 'PayPal',
      applepay: 'Apple Pay',
      googlepay: 'Google Pay',
      card: 'Credit/Debit Card',
    };
    return labels[paymentMethod] || 'Credit/Debit Card';
  };

  return (
    <div className="space-y-4">
      <h3 className="font-primary text-lg font-bold text-gray-800">Order Review</h3>
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <p className="font-secondary text-sm text-gray-600">Items ({items.length})</p>
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />
              <div>
                <p className="font-primary font-semibold text-sm text-gray-800">{item.name}</p>
                <p className="font-secondary text-xs text-gray-500">Qty: {item.quantity}</p>
              </div>
            </div>
            <span className="font-primary font-bold text-gray-800">${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="border-t border-gray-200 pt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-semibold">${total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Tax</span>
            <span className="font-semibold">${(total * 0.08).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Payment Method</span>
            <span className="font-medium capitalize">{getPaymentLabel()}</span>
          </div>
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-blue-600">${(total * 1.08).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
        <FaLock className="text-blue-600 mt-0.5" />
        <div>
          <p className="font-secondary text-sm text-gray-700">Secure Payment</p>
          <p className="font-secondary text-xs text-gray-500">Your payment information is encrypted and secure</p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-4 py-3 border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors duration-200 font-primary font-semibold"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isProcessing}
          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {isProcessing ? (
            <>
              <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
              Processing...
            </>
          ) : (
            <>
              <FaCheckCircle />
              Place Order
            </>
          )}
        </button>
      </div>
    </div>
  );
}